import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Statistic, Spin, Table, DatePicker, Space, Alert, Tag, Rate, Tabs, Drawer, Avatar } from 'antd';
import {
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SmileOutlined,
  CalendarOutlined,
  PlusCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  FilterOutlined,
  StarFilled,
} from '@ant-design/icons';
import {
  PieChart, Pie, Cell, LineChart, Line, BarChart, Bar, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import dayjs from 'dayjs';
import PageLayout from '../components/layout/PageLayout';
import api from '../libs/api';

const { RangePicker } = DatePicker;

const PIE_COLORS = ['#FA5252', '#f5b759', '#54A2CC', '#05A677', '#845EC2', '#FF6F91', '#0081CF', '#FFC75F'];
const STATUS_COLORS = { new: '#54A2CC', open: '#f5b759', pending: '#845EC2', hold: '#FF6F91', solved: '#05A677', closed: '#999' };
const now = dayjs();
const fyStart = now.month() >= 6
  ? dayjs().year(now.year()).month(6).startOf('month')
  : dayjs().year(now.year() - 1).month(6).startOf('month');

const DATE_PRESETS = [
  { label: 'Last 7 Days', value: [now.subtract(7, 'day'), now] },
  { label: 'Last 30 Days', value: [now.subtract(30, 'day'), now] },
  { label: 'Last 90 Days', value: [now.subtract(90, 'day'), now] },
  { label: 'Last 6 Months', value: [now.subtract(6, 'month'), now] },
  { label: 'Last 12 Months', value: [now.subtract(12, 'month'), now] },
  { label: 'This Quarter', value: [now.startOf('quarter'), now] },
  { label: 'This FY', value: [fyStart, now] },
];

function fmtHrs(v) {
  if (v == null) return '-';
  if (v >= 24) return `${(v / 24).toFixed(1)}d`;
  return `${v.toFixed(1)}h`;
}

function fmtDays(v) {
  if (v == null) return '-';
  return `${v.toFixed(1)}d`;
}

// Reusable efficiency stats card
function EfficiencyCard({ title, titleColor, eff, csat }) {
  return (
    <Card size="small" title={<span style={{ color: titleColor }}>{title}</span>}>
      <Row gutter={[12, 8]}>
        <Col span={12}>
          <Statistic title="Avg Resolution" value={fmtHrs(eff?.avgResolutionHrs)} valueStyle={{ fontSize: 18 }}
            prefix={<ClockCircleOutlined style={{ color: '#f5b759', fontSize: 14 }} />} />
        </Col>
        <Col span={12}>
          <Statistic title="Median Resolution" value={fmtHrs(eff?.medianResolutionHrs)} valueStyle={{ fontSize: 18 }}
            prefix={<ClockCircleOutlined style={{ color: '#845EC2', fontSize: 14 }} />} />
        </Col>
        <Col span={12}>
          <Statistic title="P90 Resolution" value={fmtHrs(eff?.p90ResolutionHrs)} valueStyle={{ fontSize: 18 }}
            prefix={<ClockCircleOutlined style={{ color: '#FA5252', fontSize: 14 }} />} />
        </Col>
        <Col span={12}>
          <Statistic title="Avg First Response" value={fmtHrs(eff?.avgFirstResponseHrs)} valueStyle={{ fontSize: 18 }}
            prefix={<ClockCircleOutlined style={{ color: '#54A2CC', fontSize: 14 }} />} />
        </Col>
        <Col span={12}>
          <Statistic title="CSAT Score" value={csat?.score != null ? csat.score : '-'}
            suffix={csat?.score != null ? '%' : ''} valueStyle={{ fontSize: 18, color: csat?.score != null ? (csat.score >= 80 ? '#05A677' : csat.score >= 50 ? '#f5b759' : '#FA5252') : undefined }}
            prefix={<SmileOutlined style={{ color: '#05A677', fontSize: 14 }} />} />
        </Col>
        <Col span={12}>
          <div style={{ fontSize: 12, color: '#999', paddingTop: 8 }}>
            {eff?.sampleSize != null ? `${eff.sampleSize} resolved` : ''}
            {csat?.total ? ` · ${csat.total} rated` : ''}
          </div>
        </Col>
      </Row>
    </Card>
  );
}

export default function Complaints() {
  const [dateRange, setDateRange] = useState([now.subtract(30, 'day'), now]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [reviewsData, setReviewsData] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  // AI Agent tab — independent date range + separate fetch
  const [activeTab, setActiveTab] = useState('zendesk');
  /**
   * The AI tab opens on six months.
   *
   * A month of rated cases is a handful of rows — too few to see whether the
   * AI's ratings are drifting. The searches behind this are chunked by week
   * and now run through a concurrency gate with rate-limit retries, so a
   * longer window costs time on the first load and nothing after it, the
   * result being cached. Twelve months is one preset further if wanted.
   */
  const [aiDateRange, setAiDateRange] = useState([now.subtract(6, 'month'), now]);
  const [aiData, setAiData] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);

  // Ticket detail drawer
  const [drawerTicketId, setDrawerTicketId] = useState(null);
  const [drawerData, setDrawerData] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState(null);

  const openTicketDrawer = useCallback(async (id) => {
    setDrawerTicketId(id);
    setDrawerData(null);
    setDrawerError(null);
    setDrawerLoading(true);
    try {
      const res = await api.get(`/api/complaints/ticket/${id}`);
      setDrawerData(res.data?.data || null);
    } catch (err) {
      setDrawerError(err.response?.data?.message || 'Failed to load ticket detail');
    }
    setDrawerLoading(false);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerTicketId(null);
    setDrawerData(null);
    setDrawerError(null);
  }, []);

  // Fetch Google Reviews (independent of date range)
  useEffect(() => {
    async function fetchReviews() {
      setReviewsLoading(true);
      try {
        const res = await api.get('/api/reviews/google');
        if (res.data.data) setReviewsData(res.data.data);
      } catch { /* ignore - reviews are optional */ }
      setReviewsLoading(false);
    }
    fetchReviews();
  }, []);

  const fetchData = useCallback(async (range, org) => {
    if (!range?.[0] || !range?.[1]) return;
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const params = { start: range[0].format('YYYY-MM-DD'), end: range[1].format('YYYY-MM-DD') };
      if (org) params.org = org;
      const res = await api.get('/api/complaints/summary', { params, timeout: 120000 });
      if (res.data.data) {
        setData(res.data.data);
      } else {
        setMessage(res.data.message || 'No data available');
        setData(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load Zendesk metrics');
      setData(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(dateRange, selectedOrg); }, [dateRange, selectedOrg, fetchData]);

  const fetchAiData = useCallback(async (range) => {
    if (!range?.[0] || !range?.[1]) return;
    setAiLoading(true);
    setAiError(null);
    try {
      const params = { start: range[0].format('YYYY-MM-DD'), end: range[1].format('YYYY-MM-DD') };
      const res = await api.get('/api/complaints/summary', { params, timeout: 120000 });
      setAiData(res.data?.data || null);
    } catch (err) {
      setAiError(err.response?.data?.message || 'Failed to load AI Agent metrics');
      setAiData(null);
    }
    setAiLoading(false);
  }, []);

  const aiRangeKey = aiDateRange ? `${aiDateRange[0]?.format('YYYY-MM-DD')}_${aiDateRange[1]?.format('YYYY-MM-DD')}` : '';
  useEffect(() => {
    if (activeTab === 'ai' && aiDateRange) fetchAiData(aiDateRange);
  }, [activeTab, aiRangeKey, fetchAiData]);

  const o = data?.overview || {};
  const bs = data?.backlogStats || {};

  const orgColumns = [
    { title: 'Organization', dataIndex: 'orgName', key: 'orgName', ellipsis: true, sorter: (a, b) => a.orgName.localeCompare(b.orgName) },
    { title: 'Total', dataIndex: 'total', key: 'total', width: 80, align: 'right', sorter: (a, b) => a.total - b.total, defaultSortOrder: 'descend' },
    { title: 'Open', dataIndex: 'open', key: 'open', width: 70, align: 'right', render: (v) => v || '-' },
    { title: 'Solved', dataIndex: 'solved', key: 'solved', width: 80, align: 'right', render: (v) => v || '-' },
    {
      title: 'Avg Resolution', dataIndex: 'avgResolutionHrs', key: 'avgRes', width: 120, align: 'right',
      render: fmtHrs,
      sorter: (a, b) => (a.avgResolutionHrs || 9999) - (b.avgResolutionHrs || 9999),
    },
  ];

  // ===== Google Reviews tab content =====
  const reviewsContent = (
    <Spin spinning={reviewsLoading}>
        {reviewsData && (
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            {reviewsData.map((biz) => (
              <Col xs={24} md={8} key={biz.name}>
                <Card
                  size="small"
                  title={biz.displayName || biz.name}
                  extra={biz.rating != null ? (
                    <span style={{ fontWeight: 700, color: '#f5b759', fontSize: 16 }}>
                      {biz.rating.toFixed(1)} <StarFilled />
                    </span>
                  ) : null}
                >
                  {biz.rating != null ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <Rate disabled allowHalf value={biz.rating} style={{ fontSize: 14 }} />
                        <span style={{ color: '#999', fontSize: 12 }}>{biz.userRatingCount.toLocaleString()} reviews</span>
                      </div>
                      {biz.reviews.map((review, i) => (
                        <div key={i} style={{ padding: '6px 0', borderTop: '1px solid #f0f0f0' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                            <span style={{ fontWeight: 600, fontSize: 12 }}>{review.author}</span>
                            <Rate disabled value={review.rating} style={{ fontSize: 10 }} />
                          </div>
                          {review.text && (
                            <div style={{
                              fontSize: 12, color: '#666', lineHeight: 1.4,
                              overflow: 'hidden', textOverflow: 'ellipsis',
                              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
                            }}>
                              {review.text}
                            </div>
                          )}
                          <div style={{ fontSize: 11, color: '#bbb', marginTop: 2 }}>{review.relativeTime}</div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div style={{ color: '#999', padding: '20px 0', textAlign: 'center' }}>
                      {biz.error ? 'Unable to load reviews' : 'No reviews available'}
                    </div>
                  )}
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Spin>

  );

  // ===== Zendesk Metrics tab content =====
  const zendeskContent = (
    <>
      {error && <Alert message={error} type="error" showIcon closable style={{ marginBottom: 16 }} onClose={() => setError(null)} />}
      {message && <Alert message={message} type="info" showIcon style={{ marginBottom: 16 }} />}

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space align="center" wrap>
          <CalendarOutlined style={{ color: '#54A2CC' }} />
          <RangePicker
            value={dateRange}
            onChange={(v) => v && setDateRange(v)}
            presets={DATE_PRESETS}
            allowClear={false}
            size="small"
          />
          {selectedOrg && (
            <Tag
              icon={<FilterOutlined />}
              color="blue"
              closable
              onClose={() => setSelectedOrg(null)}
              style={{ fontSize: 13, padding: '2px 8px' }}
            >
              {selectedOrg}
            </Tag>
          )}
        </Space>
      </Card>

      <Spin spinning={loading} tip="Fetching Zendesk data...">
        {data && (
          <>
            {/* KPI Row: Created | Solved | Unsolved | Backlog */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12} md={6}>
                <Card style={{ minHeight: 110 }}>
                  <Statistic title="Created in Period" value={o.created || 0}
                    prefix={<PlusCircleOutlined style={{ color: '#54A2CC' }} />} />
                  {o.created > 0 && (
                    <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                      {o.created - (o.unsolved || 0)} resolved ({Math.round((o.created - (o.unsolved || 0)) / o.created * 100)}%)
                      {o.createdReopened > 0 && <span style={{ color: '#FA5252' }}> · {o.createdReopened} reopened</span>}
                    </div>
                  )}
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card style={{ minHeight: 110 }}>
                  <Statistic title="Solved in Period" value={o.solved || 0}
                    prefix={<CheckCircleOutlined style={{ color: '#05A677' }} />} />
                  {o.solved > 0 && (
                    <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                      {o.solvedReopened > 0
                        ? <span style={{ color: '#FA5252' }}>{o.solvedReopened} reopened ({Math.round(o.solvedReopened / o.solved * 100)}%)</span>
                        : <span>0 reopened</span>}
                    </div>
                  )}
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card style={{ minHeight: 110 }}>
                  <Statistic title="Unsolved (Created)" value={o.unsolved || 0}
                    prefix={<WarningOutlined style={{ color: '#FA5252' }} />}
                    valueStyle={o.unsolved > 0 ? { color: '#FA5252' } : undefined} />
                  <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>Created but not yet resolved</div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card style={{ minHeight: 110 }}>
                  <Statistic title="Backlog (All Open)" value={o.openTicketCount || 0}
                    prefix={<ExclamationCircleOutlined style={{ color: '#FF6F91' }} />} />
                  <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                    P50: {fmtDays(bs.p50Days)} · P90: {fmtDays(bs.p90Days)}
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Efficiency: Created vs Solved side by side */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} lg={12}>
                <EfficiencyCard
                  title="Created in Period (resolved so far)"
                  titleColor="#54A2CC"
                  eff={data.createdEfficiency}
                  csat={data.createdCSAT}
                />
              </Col>
              <Col xs={24} lg={12}>
                <EfficiencyCard
                  title="Solved in Period"
                  titleColor="#05A677"
                  eff={data.solvedEfficiency}
                  csat={data.solvedCSAT}
                />
              </Col>
            </Row>

            {/* Ticket Volume Trend (Daily) — grouped bars */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={24}>
                <Card title="Ticket Volume Trend (Daily)" size="small">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data.trend || []} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" fontSize={10} tickFormatter={(d) => dayjs(d).format('DD MMM')} />
                      <YAxis allowDecimals={false} fontSize={11} />
                      <Tooltip labelFormatter={(d) => dayjs(d).format('DD MMM YYYY')} />
                      <Legend />
                      <Bar dataKey="created" fill="#FA5252" name="Created" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="solved" fill="#05A677" name="Solved" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            </Row>

            {/* Charts Row 2: Status + Priority */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} lg={12}>
                <Card title="Status Distribution (Created)" size="small">
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={(data.statusDistribution || []).filter(s => s.count > 0)} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={90}
                        label={({ status, count, percent }) => `${status} (${count}) ${(percent * 100).toFixed(0)}%`} fontSize={11}>
                        {(data.statusDistribution || []).filter(s => s.count > 0).map((entry, i) => (
                          <Cell key={i} fill={STATUS_COLORS[entry.status] || PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card title="Priority Distribution (Created)" size="small">
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie data={data.priorityDistribution || []} dataKey="count" nameKey="priority" cx="50%" cy="50%" outerRadius={90}
                        label={({ priority, count, percent }) => count > 0 ? `${priority} (${count}) ${(percent * 100).toFixed(0)}%` : ''} fontSize={11}>
                        {(data.priorityDistribution || []).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            </Row>

            {/* Charts Row 4: Weekly Resolution Time + Weekly CSAT Trend */}
            {((data.weeklyEfficiency || []).length > 1 || (data.weeklyCSAT || []).length > 1) && (
              <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                {(data.weeklyEfficiency || []).length > 1 && (
                  <Col span={24}>
                    <Card title="Weekly Resolution & Response Time" size="small">
                      <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={data.weeklyEfficiency} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="week" fontSize={10} tickFormatter={(d) => dayjs(d).format('DD MMM')} />
                          <YAxis fontSize={11} unit="h" />
                          <Tooltip labelFormatter={(d) => `Week of ${dayjs(d).format('DD MMM YYYY')}`}
                            formatter={(v, name) => [v != null ? `${v}h` : '-', name]} />
                          <Legend />
                          <Line type="monotone" dataKey="avgResolutionHrs" stroke="#f5b759" name="Avg Resolution" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                          <Line type="monotone" dataKey="avgFirstResponseHrs" stroke="#845EC2" name="Avg First Response" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                        </LineChart>
                      </ResponsiveContainer>
                    </Card>
                  </Col>
                )}
                {(data.weeklyCSAT || []).length > 1 && (
                  <Col xs={24} lg={10}>
                    <Card title="Weekly CSAT Score" size="small">
                      <ResponsiveContainer width="100%" height={280}>
                        <LineChart data={data.weeklyCSAT} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="week" fontSize={10} tickFormatter={(d) => dayjs(d).format('DD MMM')} />
                          <YAxis fontSize={11} unit="%" domain={[0, 100]} />
                          <Tooltip labelFormatter={(d) => `Week of ${dayjs(d).format('DD MMM YYYY')}`}
                            formatter={(v) => [v != null ? `${v}%` : '-', 'CSAT']} />
                          <Line type="monotone" dataKey="score" stroke="#05A677" name="CSAT %" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                        </LineChart>
                      </ResponsiveContainer>
                    </Card>
                  </Col>
                )}
              </Row>
            )}

            {/* Organization Breakdown Table */}
            <Card title={`Tickets by Organization (${(data.organizationBreakdown || []).length})`} size="small"
              extra={selectedOrg && <Tag color="blue">{selectedOrg}</Tag>}>
              <Table
                dataSource={(data.organizationBreakdown || []).map((r, i) => ({ ...r, key: i }))}
                columns={orgColumns}
                pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: [20, 50, 100] }}
                size="small"
                onRow={(record) => ({
                  onClick: () => setSelectedOrg(selectedOrg === record.orgName ? null : record.orgName),
                  style: { cursor: 'pointer', backgroundColor: selectedOrg === record.orgName ? '#e6f4ff' : undefined },
                })}
              />
            </Card>
          </>
        )}
      </Spin>
    </>
  );

  // ===== AI Agent tab content =====
  const aiLabels = aiData?.aiAgentLabels || {};
  const allRatedCases = aiData?.ratedCases || [];
  const adminAiRatedCases = allRatedCases.filter(c => c.assigneeType === 'admin_ai');
  const humanRatedCases  = allRatedCases.filter(c => c.assigneeType === 'human');
  const aiAssigneeRows = (aiData?.assigneeBreakdown || []).filter(r =>
    /\bautomation\b|\bai agent\b/i.test(r.assigneeName || '')
  );
  const aiContent = (
    <>
      {aiError && <Alert message={aiError} type="error" showIcon closable style={{ marginBottom: 16 }} onClose={() => setAiError(null)} />}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space align="center" wrap>
          <CalendarOutlined style={{ color: '#54A2CC' }} />
          <RangePicker
            value={aiDateRange}
            onChange={(v) => v && setAiDateRange(v)}
            presets={DATE_PRESETS}
            allowClear={false}
            size="small"
          />
          {aiLabels.automation && <Tag color="blue">Automation = {aiLabels.automation}</Tag>}
          {aiLabels.adminAi && <Tag color="purple">Admin AI = {aiLabels.adminAi}</Tag>}
        </Space>
      </Card>

      <Spin spinning={aiLoading} tip="Fetching AI agent data...">
        {aiData && (
          <>
            {/* Weekly volume + share */}
            {(aiData.aiAgentWeekly || []).length > 0 && (
              <Card
                size="small"
                style={{ marginBottom: 16 }}
                title="AI Agent — Weekly Tickets Solved & Share of Total"
              >
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={aiData.aiAgentWeekly} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="week" fontSize={10} tickFormatter={(d) => dayjs(d).format('DD MMM')} />
                    <YAxis yAxisId="count" fontSize={11} allowDecimals={false} />
                    <YAxis yAxisId="pct" orientation="right" fontSize={11} unit="%" domain={[0, 100]} />
                    <Tooltip labelFormatter={(d) => `Week of ${dayjs(d).format('DD MMM YYYY')}`}
                      formatter={(v, name) => name === 'AI share %' ? [v != null ? `${v.toFixed(1)}%` : '-', name] : [v, name]} />
                    <Legend />
                    <Bar yAxisId="count" dataKey="automation" stackId="all" fill="#54A2CC" name={aiLabels.automation || 'Automation'} />
                    <Bar yAxisId="count" dataKey="adminAi"    stackId="all" fill="#845EC2" name={aiLabels.adminAi || 'Admin AI'} />
                    <Bar yAxisId="count" dataKey="human"      stackId="all" fill="#f5b759" name="Human agents" radius={[3, 3, 0, 0]} />
                    <Line yAxisId="pct" type="monotone" dataKey="aiSharePct" stroke="#FA5252" strokeWidth={2} name="AI share %" dot={{ r: 3 }} connectNulls />
                  </ComposedChart>
                </ResponsiveContainer>
              </Card>
            )}

            {/* Assignee activity — only AI agents */}
            <Card
              size="small"
              title={`Assignee activity — AI agents only (${aiAssigneeRows.length})`}
              extra={<span style={{ fontSize: 11, color: '#999' }}>All assignees available in the Zendesk tab</span>}
              style={{ marginBottom: 16 }}
            >
              <Table
                dataSource={aiAssigneeRows.map((r, i) => ({ ...r, key: i }))}
                size="small"
                pagination={false}
                scroll={{ x: 1100 }}
                columns={[
                  { title: 'Assignee name', dataIndex: 'assigneeName', width: 220, ellipsis: true },
                  { title: 'Solved tickets', dataIndex: 'solved', align: 'right', width: 120,
                    sorter: (a, b) => (a.solved || 0) - (b.solved || 0), defaultSortOrder: 'descend' },
                  { title: 'First reply time (hrs)', dataIndex: 'firstReplyHrs', align: 'right', width: 140,
                    render: (v) => v != null ? `${v.toFixed(1)} hrs` : '-' },
                  { title: 'Requester wait time (hrs)', dataIndex: 'requesterWaitHrs', align: 'right', width: 160,
                    render: (v) => v != null ? `${v.toFixed(1)} hrs` : '-' },
                  { title: 'Last assignment to resolution (hrs)', dataIndex: 'lastAssignToResHrs', align: 'right', width: 200,
                    render: (v) => v != null ? `${v.toFixed(1)} hrs` : '-' },
                  { title: 'Full resolution time (hrs)', dataIndex: 'fullResolutionHrs', align: 'right', width: 160,
                    render: (v) => v != null ? `${v.toFixed(1)} hrs` : '-' },
                  { title: '% Satisfaction', dataIndex: 'satisfactionPct', align: 'right', width: 130,
                    render: (v) => v != null ? `${v.toFixed(1)}%` : '-' },
                  { title: '% One-touch', dataIndex: 'oneTouchPct', align: 'right', width: 130,
                    render: (v) => v != null ? `${v.toFixed(1)}%` : '-' },
                ]}
              />
            </Card>

            {/* Rated cases — sub-tabs split by assignee type */}
            {(() => {
              const ratedColumns = (showAssignee, assigneeOpts = []) => [
                {
                  title: 'Rating', dataIndex: 'score', width: 90,
                  filters: [{ text: 'Good', value: 'good' }, { text: 'Bad', value: 'bad' }],
                  onFilter: (v, r) => r.score === v,
                  defaultFilteredValue: ['bad', 'good'],
                  render: (v) => v === 'good'
                    ? <Tag color="green">😊 Good</Tag>
                    : <Tag color="red">😞 Bad</Tag>,
                },
                {
                  title: 'Ticket', dataIndex: 'ticketId', width: 110,
                  render: (id) => (
                    <a onClick={(e) => { e.preventDefault(); openTicketDrawer(id); }} href="#">
                      #{id}
                    </a>
                  ),
                },
                ...(showAssignee ? [{
                  title: 'Assignee', dataIndex: 'assigneeName', width: 180, ellipsis: true,
                  filters: assigneeOpts,
                  filterSearch: true,
                  onFilter: (val, r) => r.assigneeName === val,
                }] : []),
                { title: 'Subject', dataIndex: 'subject', ellipsis: true },
                {
                  title: 'Customer comment', dataIndex: 'comment',
                  render: (v) => v
                    ? <span style={{ fontStyle: 'italic', color: '#444' }}>"{v}"</span>
                    : <span style={{ color: '#bbb' }}>(no comment)</span>,
                },
                {
                  title: 'Rated at', dataIndex: 'ratedAt', width: 130,
                  render: (v) => v ? dayjs(v).format('DD MMM YYYY') : '-',
                  sorter: (a, b) => (a.ratedAt || '').localeCompare(b.ratedAt || ''),
                  // Newest first. The server hands these over worst-rating
                  // first, which is the right order for a review queue and the
                  // wrong one for reading down a feed.
                  defaultSortOrder: 'descend',
                },
              ];

              const renderTable = (rows, emptyMsg, withAssignee) => {
                if (rows.length === 0) {
                  return <div style={{ color: '#999', padding: 16, textAlign: 'center' }}>{emptyMsg}</div>;
                }
                const assigneeOpts = withAssignee
                  ? [...new Set(rows.map(r => r.assigneeName).filter(Boolean))]
                      .sort()
                      .map(n => ({ text: n, value: n }))
                  : [];
                return (
                  <Table
                    dataSource={rows.map((c, i) => ({ ...c, key: i }))}
                    size="small"
                    pagination={{ defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: [10, 20, 50, 100] }}
                    rowClassName={(r) => r.score === 'bad' ? 'csat-bad' : 'csat-good'}
                    columns={ratedColumns(withAssignee, assigneeOpts)}
                  />
                );
              };

              return (
                <Card size="small" title="Rated Cases (customer feedback)">
                  <Tabs
                    defaultActiveKey="ai"
                    items={[
                      {
                        key: 'ai',
                        label: (
                          <span>AI <Tag color="green" style={{ marginLeft: 6 }}>{adminAiRatedCases.filter(c => c.score === 'good').length}</Tag>
                          <Tag color="red">{adminAiRatedCases.filter(c => c.score === 'bad').length}</Tag></span>
                        ),
                        children: renderTable(
                          adminAiRatedCases,
                          'No good/bad ratings on Admin AI Agent tickets in this period.',
                          false,
                        ),
                      },
                      {
                        key: 'human',
                        label: (
                          <span>Human agents <Tag color="green" style={{ marginLeft: 6 }}>{humanRatedCases.filter(c => c.score === 'good').length}</Tag>
                          <Tag color="red">{humanRatedCases.filter(c => c.score === 'bad').length}</Tag></span>
                        ),
                        children: renderTable(
                          humanRatedCases,
                          'No good/bad ratings on human-handled tickets in this period.',
                          true,
                        ),
                      },
                    ]}
                  />
                </Card>
              );
            })()}
          </>
        )}
      </Spin>
    </>
  );

  return (
    <PageLayout>
      <div className="page-header">
        <h1>Customer Service</h1>
        <p>Zendesk after-sales ticket handling, AI agent performance and Google Reviews</p>
      </div>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          { key: 'zendesk', label: 'Zendesk Metrics', children: zendeskContent },
          { key: 'ai',      label: 'AI Agent',        children: aiContent },
          { key: 'reviews', label: 'Google Reviews',  children: reviewsContent },
        ]}
      />

      <Drawer
        title={drawerData ? `#${drawerData.id} · ${drawerData.subject || '(no subject)'}` : (drawerTicketId ? `#${drawerTicketId}` : '')}
        width={680}
        open={!!drawerTicketId}
        onClose={closeDrawer}
        extra={drawerData?.url
          ? <a href={drawerData.url} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>Open in Zendesk →</a>
          : null}
      >
        <Spin spinning={drawerLoading}>
          {drawerError && <Alert message={drawerError} type="error" showIcon style={{ marginBottom: 16 }} />}
          {drawerData && (
            <>
              <Space wrap size={[6, 6]} style={{ marginBottom: 12 }}>
                <Tag color={drawerData.status === 'solved' || drawerData.status === 'closed' ? 'green' : 'blue'}>
                  Status: {drawerData.status}
                </Tag>
                {drawerData.priority && <Tag>Priority: {drawerData.priority}</Tag>}
                {drawerData.satisfactionRating?.score === 'good' && <Tag color="green">😊 Good rating</Tag>}
                {drawerData.satisfactionRating?.score === 'bad' && <Tag color="red">😞 Bad rating</Tag>}
                {drawerData.metrics?.firstReplyHrs != null && (
                  <Tag color="default">First reply {drawerData.metrics.firstReplyHrs}h</Tag>
                )}
                {drawerData.metrics?.fullResolutionHrs != null && (
                  <Tag color="default">Full resolution {drawerData.metrics.fullResolutionHrs}h</Tag>
                )}
              </Space>

              {/* Participants */}
              <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 6, fontSize: 12, marginBottom: 12 }}>
                <div style={{ color: '#999' }}>Requester:</div>
                <div>{drawerData.requester?.name || `User ${drawerData.requester?.id || '?'}`} {drawerData.requester?.email && <span style={{ color: '#888' }}>&lt;{drawerData.requester.email}&gt;</span>}</div>
                <div style={{ color: '#999' }}>Assignee:</div>
                <div>{drawerData.assignee?.name || '—'} {drawerData.assignee?.role && <Tag style={{ marginLeft: 4 }}>{drawerData.assignee.role}</Tag>}</div>
                <div style={{ color: '#999' }}>Created:</div>
                <div>{drawerData.createdAt ? dayjs(drawerData.createdAt).format('DD MMM YYYY HH:mm') : '—'}</div>
                <div style={{ color: '#999' }}>Updated:</div>
                <div>{drawerData.updatedAt ? dayjs(drawerData.updatedAt).format('DD MMM YYYY HH:mm') : '—'}</div>
                {drawerData.tags?.length > 0 && (
                  <>
                    <div style={{ color: '#999' }}>Tags:</div>
                    <div>{drawerData.tags.map(t => <Tag key={t} style={{ marginBottom: 4 }}>{t}</Tag>)}</div>
                  </>
                )}
              </div>

              {drawerData.satisfactionRating?.comment && (
                <Alert
                  type={drawerData.satisfactionRating.score?.startsWith('good') ? 'success' : 'error'}
                  message={`Customer rating comment`}
                  description={<span style={{ fontStyle: 'italic' }}>"{drawerData.satisfactionRating.comment}"</span>}
                  style={{ marginBottom: 16 }}
                  showIcon
                />
              )}

              {/* Conversation */}
              <div style={{ fontSize: 13, fontWeight: 600, color: '#262B40', marginBottom: 8 }}>
                Conversation ({drawerData.comments?.length || 0})
              </div>
              {(drawerData.comments || []).map((c, i) => {
                const isFirst = i === 0;
                const isInternal = c.publicComment === false;
                const author = c.author?.name || c.author?.email || `User ${c.author?.id || '?'}`;
                const isAgent = c.author?.role === 'agent' || c.author?.role === 'admin';
                return (
                  <div
                    key={c.id || i}
                    style={{
                      borderLeft: `3px solid ${isInternal ? '#f5b759' : isAgent ? '#54A2CC' : '#05A677'}`,
                      background: isInternal ? '#fffbe6' : '#fafafa',
                      borderRadius: 6,
                      padding: '8px 12px',
                      marginBottom: 8,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      {c.author?.photo
                        ? <Avatar src={c.author.photo} size={22} />
                        : <Avatar size={22} style={{ background: isAgent ? '#54A2CC' : '#05A677', fontSize: 11 }}>{(author[0] || '?').toUpperCase()}</Avatar>}
                      <span style={{ fontSize: 12, fontWeight: 600 }}>{author}</span>
                      {isAgent && <Tag color="blue" style={{ marginLeft: 0 }}>{c.author.role}</Tag>}
                      {isFirst && <Tag color="default">first message</Tag>}
                      {isInternal && <Tag color="orange">internal note</Tag>}
                      <span style={{ marginLeft: 'auto', fontSize: 11, color: '#999' }}>
                        {c.createdAt ? dayjs(c.createdAt).format('DD MMM HH:mm') : ''}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: '#333', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {c.body || '(empty)'}
                    </div>
                    {c.attachments?.length > 0 && (
                      <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
                        Attachments: {c.attachments.map((a, j) => (
                          <a key={j} href={a.contentUrl} target="_blank" rel="noreferrer" style={{ marginRight: 8 }}>
                            {a.fileName}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </Spin>
      </Drawer>
    </PageLayout>
  );
}
